import React, { useEffect, useState } from 'react';
import { FiCalendar, FiList, FiMessageSquare, FiPhone, FiX } from 'react-icons/fi';
import Icon from '../common/Icon';
import { callLogService } from '../../services/callLogService';
import { CallLog } from '../../types';
import { formatDateTime } from '../../utils/dateUtils';
import { getSettingColorClass } from '../../utils/settingColors';

interface AllCallLogButtonProps {
  entityType: 'lead' | 'customer' | 'tender';
  entityId: number;
  entityName?: string;
  className?: string;
}

const AllCallLogButton: React.FC<AllCallLogButtonProps> = ({
  entityType,
  entityId,
  entityName,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<CallLog[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    callLogService
      .getByEntity(entityType, entityId, { all: true })
      .then((data) => {
        if (!cancelled) setLogs(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setLogs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, entityType, entityId]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 border border-indigo-300 text-indigo-800 bg-indigo-50 rounded-md hover:bg-indigo-100 text-sm font-medium ${className}`}
      >
        <Icon component={FiList} className="w-4 h-4" />
        All Call Log
      </button>

      {open && (
        <div className="fixed inset-0 bg-gray-700 bg-opacity-60 overflow-y-auto h-full w-full z-[70] p-3 sm:p-4">
          <div className="relative top-2 sm:top-8 mx-auto p-4 sm:p-5 border w-full max-w-2xl shadow-xl rounded-lg bg-white max-h-[92vh] flex flex-col">
            <div className="flex justify-between items-start gap-3 mb-3">
              <div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900">All Call Log</h3>
                {entityName && (
                  <p className="text-sm text-gray-600 mt-0.5">{entityName}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Full history for this record, including other sales officers.</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
                aria-label="Close"
              >
                <Icon component={FiX} className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 min-h-0 pr-1">
              {loading ? (
                <div className="text-center py-8 text-sm text-gray-500">Loading call history...</div>
              ) : logs.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-500">No call logs yet</div>
              ) : (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <div key={log.id} className="border-l-4 border-indigo-500 pl-4 py-2 bg-gray-50 rounded-r-md">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-1 mb-1">
                        <div className="flex items-center space-x-2">
                          <Icon component={FiPhone} className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-semibold text-gray-900">
                            {formatDateTime(log.call_date, log.call_time)}
                          </span>
                        </div>
                        {log.called_by_name && (
                          <span className="text-xs text-gray-500">
                            by {log.called_by_name}
                            {log.logged_by_name && log.logged_by_name !== log.called_by_name && (
                              <span className="text-gray-400"> (logged by {log.logged_by_name})</span>
                            )}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 mb-1">
                        {log.call_status && (
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getSettingColorClass(log.call_status_color_code)} border`}>
                            {log.call_status}
                          </span>
                        )}
                        {log.call_outcome && (
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-800">
                            {log.call_outcome}
                          </span>
                        )}
                      </div>
                      {log.notes && (
                        <div className="text-sm text-gray-700 mt-1 flex items-start space-x-2">
                          <Icon component={FiMessageSquare} className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <p className="flex-1">{log.notes}</p>
                        </div>
                      )}
                      {(log.next_call_date || log.next_call_notes) && (
                        <div className="mt-2 text-xs text-indigo-600">
                          {log.next_call_date && (
                            <div className="flex items-center space-x-1">
                              <Icon component={FiCalendar} className="w-3 h-3" />
                              <span>Next call: {formatDateTime(log.next_call_date)}</span>
                            </div>
                          )}
                          {log.next_call_notes && (
                            <p className="mt-0.5 pl-4 text-indigo-800">Next call notes: {log.next_call_notes}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end pt-3 mt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AllCallLogButton;
